(() => {
  const script = document.querySelector('script[data-rdtn-shipping-merch]');
  if (!script) return;

  const country = (script.dataset.country || '').toUpperCase();
  const mix3Url = script.dataset.mix3Url || '/collections/mix-3-ship-free';
  const shippingUrl = script.dataset.shippingUrl || '/policies/shipping-policy';
  const europe79 = new Set(['AT','BE','BG','HR','CY','CZ','DK','EE','FI','FR','DE','GR','HU','IE','IT','LV','LT','LU','MT','NL','PL','PT','RO','SK','SI','ES','SE','GB']);
  const freeNoMinimum = new Set(['TW','HK','MO']);
  const threshold159 = new Set(['CA','AU','NZ']);
  let syncTimer;
  let syncInFlight = false;
  let syncPending = false;
  let syncRevision = 0;
  let latestCart = null;
  const recommendationCache = new Map();

  const englishCopy = {
    "mainland_label": "Mainland U.S.",
    "us_cart_next": "Add {{ amount }} to save {{ saving }} on Standard shipping.",
    "us_cart_free": "Free Standard shipping unlocked for Mainland U.S.",
    "us_cart_scope": "Mainland U.S. only · Discounted merchandise subtotal. Alaska, Hawaii, territories and other destinations: rates at checkout.",
    "us_cart_rate": "Standard shipping: {{ current }} → {{ next }} at {{ goal }}.",
    "us_cart_subtotal": "After discounts: {{ amount }}",
    "us_cart_related": "Complete your setup",
    "us_cart_fitment": "Related to your cart · Check vehicle fitment before adding.",
    "us_cart_add": "Add",
    "us_cart_choose": "Choose options",
    "us_cart_from": "From {{ amount }}",
    "us_cart_empty": "Mainland U.S. Standard shipping drops at $29, $79 and $159 after discounts.",
    "mainland_rates": "Mainland U.S.: Free Standard Shipping $159+ after discounts. Shipping calculated at checkout.",
    "mainland_other": "Other destinations: rates at checkout.",
    "us_duties": "U.S. orders: import duties covered and no sales tax.",
    "free_standard": "Free standard shipping.",
    "threshold": "Free standard shipping on {{ amount }} orders.",
    "regional": "Regional free-shipping offers are available.",
    "legacy_notice": "Shipping rates and free-shipping thresholds vary by destination. Final options are shown at checkout.",
    "product_us": "U.S. orders: import duties covered and no sales tax. {{ link }} for another free-shipping option.",
    "product_other": "{{ link }} for your destination.",
    "mix3_link": "Mix any {{ quantity }} eligible items",
    "see_details_link": "See shipping details",
    "cart_free_available": "Free standard shipping available.",
    "cart_unlocked": "Free standard shipping unlocked at {{ amount }}.",
    "cart_remaining": "{{ amount }} away from free standard shipping.",
    "cart_details_link": "Shipping details",
    "cart_alternative": "Another option: {{ link }}. Final eligibility is confirmed at checkout."
  };
  let translations = {};
  try {
    const data = JSON.parse(document.querySelector('script[data-rdtn-shipping-translations]')?.textContent || '{}');
    if (data && typeof data === 'object' && !Array.isArray(data)) translations = data;
  } catch (_) {}

  const escapeHtml = (value) => String(value).replace(/[&<>"']/g, (character) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character]
  );

  function copyTemplate(key) {
    const fallback = englishCopy[key];
    let value = translations[key];
    if (typeof value !== 'string' || !value.trim() || /translation[\s_]+missing|interpolation error|liquid (?:syntax )?error/i.test(value) || value === 'rdtn.quick_shipping.' + key) return fallback;
    value = value.replace(/&(amp|lt|gt|quot|#39|#x27);/gi, (entity) =>
      ({ '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'", '&#x27;': "'" })[entity.toLowerCase()]
    ).replace(/__RDTN_(AMOUNT|QUANTITY|LINK|SAVING|CURRENT|NEXT|GOAL)__/g, (_, name) => '{{ ' + name.toLowerCase() + ' }}');
    const placeholders = (text) => Array.from(text.matchAll(/{{\s*([a-z_]+)\s*}}/g), (match) => match[1]).sort().join(',');
    const remainder = value.replace(/{{\s*[a-z_]+\s*}}/g, '');
    if (placeholders(value) !== placeholders(fallback) || /{{|}}|__RDTN_[A-Z_]+__/.test(remainder)) return fallback;
    return value;
  }

  function translate(key, values = {}) {
    return copyTemplate(key).replace(/{{\s*([a-z_]+)\s*}}/g, (_, name) => String(values[name]));
  }

  function linkedCopy(key, link) {
    return copyTemplate(key).split(/{{\s*link\s*}}/g).map(escapeHtml).join(link);
  }

  function shippingRule() {
    if (country === 'US') return { threshold: null, label: translate('mainland_rates') };
    if (freeNoMinimum.has(country)) return { threshold: 0, label: translate('free_standard') };
    if (threshold159.has(country)) return { threshold: 15900, label: translate('threshold', { amount: '$159+' }) };
    if (europe79.has(country)) return { threshold: 7900, label: translate('threshold', { amount: '$79+' }) };
    return { threshold: null, label: translate('regional') };
  }

  const formatUsd = (cents) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);

  function repairLegacyShippingCopy() {
    document.querySelectorAll('p').forEach((paragraph) => {
      if (!paragraph.textContent.includes('Free worldwide shipping on all orders.')) return;
      paragraph.innerHTML = paragraph.innerHTML.replace(
        /Free worldwide shipping on all orders\.\s*(?:<br\s*\/?>(?:\s|&nbsp;)*)?FedEx Express available for \$19 USD\.\s*(?:<br\s*\/?>(?:\s|&nbsp;)*)?Free FedEx on orders over \$159 USD\./i,
        () => escapeHtml(translate('legacy_notice'))
      );
    });
  }

  function renderProductMessage() {
    const isProductPage = document.querySelector('product-information, .product-information, [data-product-id]');
    if (!isProductPage) return;

    const existingNote = document.querySelector('.rdtn-pdp-shipping-note');
    if (existingNote) return;

    const taxParagraph = Array.from(document.querySelectorAll('main p')).find((node) =>
      node.textContent.trim().startsWith('U.S. customers now enjoy 0 import tax')
    );

    const rule = shippingRule();
    const note = document.createElement('div');
    note.className = 'rdtn-pdp-shipping-note';
    note.setAttribute('role', 'note');

    let html = `<strong>${escapeHtml(rule.label)}</strong>`;
    if (country === 'US') {
      html += '<br>' + escapeHtml(translate('mainland_other')) + ' ' +
        `<a href="${shippingUrl}">${escapeHtml(translate('see_details_link'))}</a>`;
    } else {
      html += '<br>' + linkedCopy('product_other',
        `<a href="${shippingUrl}">${escapeHtml(translate('see_details_link'))}</a>`);
    }
    note.innerHTML = html;

    if (taxParagraph) {
      const wrapper = taxParagraph.closest('.text, .rte, [class*="text-"]') || taxParagraph;
      wrapper.replaceWith(note);
      return;
    }

    const addButton = document.querySelector('main button[name="add"], main [name="add"]');
    const buyArea = addButton?.closest('buy-buttons, product-form, .buy-buttons, form');
    if (buyArea) buyArea.insertAdjacentElement('afterend', note);
  }


  function mainlandMilestone(total) {
    const tiers = [
      { goal: 2900, previous: 0, current: 3900, next: 2900 },
      { goal: 7900, previous: 2900, current: 2900, next: 1900 },
      { goal: 15900, previous: 7900, current: 1900, next: 0 },
    ];
    const tier = tiers.find((item) => total < item.goal);
    return tier ? { ...tier, remaining: tier.goal - total, saving: tier.current - tier.next,
      progress: Math.max(0, Math.min(100, (total - tier.previous) / (tier.goal - tier.previous) * 100)) }
      : { goal: 15900, remaining: 0, saving: 0, current: 0, next: 0, progress: 100 };
  }

  function removeDuplicateAovNotes() {
    if (country !== 'US') return;
    document.querySelectorAll('[data-rdtn-aov-mainland-note]').forEach((note) => note.remove());
  }

  function chassisCodes(title) {
    return String(title).toUpperCase().match(/\b(?:E\d{2}|F\d{2}|G\d{2}|Z[1348]|R5[0-9])\b/g) || [];
  }

  async function renderRelatedItems(cart, revision) {
    if (country !== 'US' || cart.currency !== 'USD' || !cart.items?.length || cart.total_price >= 15900) return;
    const ids = new Set(cart.items.map((item) => item.product_id));
    const codes = new Set(cart.items.flatMap((item) => chassisCodes(item.product_title || item.title)));
    if (!codes.size) return;
    const seed = cart.items[0].product_id;
    if (!recommendationCache.has(seed)) {
      const root = window.Shopify?.routes?.root || '/';
      recommendationCache.set(seed, fetch(root + 'recommendations/products.json?product_id=' + encodeURIComponent(seed) + '&limit=10&intent=related')
        .then((response) => response.ok ? response.json() : { products: [] })
        .then((data) => data.products || []).catch(() => []));
    }
    const products = await recommendationCache.get(seed);
    if (revision !== syncRevision || latestCart !== cart) return;
    const gap = mainlandMilestone(cart.total_price).remaining;
    const related = products.filter((product) => product.available && !ids.has(product.id) &&
      chassisCodes(product.title).some((code) => codes.has(code)))
      .sort((a, b) => Math.abs(a.price - gap) - Math.abs(b.price - gap)).slice(0, 2);
    if (!related.length) return;
    const root = window.Shopify?.routes?.root || '/';
    const html = '<p class="rdtn-shipping-cart-promo__headline">' + escapeHtml(translate('us_cart_related')) + '</p>' +
      '<p class="rdtn-shipping-cart-promo__sub">' + escapeHtml(translate('us_cart_fitment')) + '</p>' +
      related.map((product) => {
        const url = root + 'products/' + encodeURIComponent(product.handle);
        const variant = product.variants?.length === 1 && product.variants[0].available ? product.variants[0] : null;
        const action = variant ? '<form action="' + root + 'cart/add" method="post"><input type="hidden" name="id" value="' + variant.id + '"><input type="hidden" name="quantity" value="1"><input type="hidden" name="return_to" value="' + root + 'cart"><button type="submit">' + escapeHtml(translate('us_cart_add')) + '</button></form>' :
          '<a class="rdtn-shipping-related__choose" href="' + url + '">' + escapeHtml(translate('us_cart_choose')) + '</a>';
        const price = product.price_varies ? translate('us_cart_from', { amount: formatUsd(product.price) }) : formatUsd(product.price);
        return '<div class="rdtn-shipping-related__item"><a href="' + url + '">' + escapeHtml(product.title) + '</a><span>' + escapeHtml(price) + '</span>' + action + '</div>';
      }).join('');
    document.querySelectorAll('[data-rdtn-related]').forEach((node) => {
      if (node.innerHTML !== html) node.innerHTML = html;
    });
  }

  function renderCartMessage(cart) {
    const containers = document.querySelectorAll('#cart-drawer .cart-totals, main .cart-totals');
    if (!containers.length) return;

    const rule = shippingRule();
    containers.forEach((container) => {
      let box = container.querySelector('.rdtn-shipping-cart-promo');
      if (!box) {
        box = document.createElement('div');
        box.className = 'rdtn-shipping-cart-promo';
        const totalContainer = container.querySelector('.cart-totals__container');
        if (totalContainer) container.insertBefore(box, totalContainer);
        else container.prepend(box);
      }

      const currency = (cart.currency || '').toUpperCase();
      if (country === 'US' && currency === 'USD') {
        const total = Math.max(0, Number(cart.total_price) || 0);
        const tier = mainlandMilestone(total);
        const headline = !cart.item_count ? translate('us_cart_empty') : tier.remaining ?
          translate('us_cart_next', { amount: formatUsd(tier.remaining), saving: formatUsd(tier.saving) }) : translate('us_cart_free');
        const rate = tier.remaining ? translate('us_cart_rate', { current: formatUsd(tier.current), next: tier.next ? formatUsd(tier.next) : 'FREE', goal: formatUsd(tier.goal) }) : '';
        const html = '<div role="status" aria-live="polite" aria-atomic="true"><p class="rdtn-shipping-cart-promo__sub">' + escapeHtml(translate('mainland_label')) + ' · ' + escapeHtml(translate('us_cart_subtotal', { amount: formatUsd(total) })) + '</p>' +
          '<p class="rdtn-shipping-cart-promo__headline">' + escapeHtml(headline) + '</p>' +
          '<div class="rdtn-shipping-cart-promo__track" aria-hidden="true"><span class="rdtn-shipping-cart-promo__fill" style="width:' + tier.progress + '%"></span></div>' +
          (rate ? '<p class="rdtn-shipping-cart-promo__sub">' + escapeHtml(rate) + '</p>' : '') + '</div>' +
          '<p class="rdtn-shipping-cart-promo__sub">' + escapeHtml(translate('us_cart_scope')) + ' <a href="' + shippingUrl + '">' + escapeHtml(translate('cart_details_link')) + '</a></p>';
        let status = box.querySelector('[data-rdtn-status]');
        if (!status) {
          box.innerHTML = '<div data-rdtn-status></div><div data-rdtn-related></div>';
          status = box.querySelector('[data-rdtn-status]');
        }
        if (status.innerHTML !== html) status.innerHTML = html;
        const signature = JSON.stringify([cart.total_price, cart.items?.map((item) => [item.key, item.quantity])]);
        if (box.dataset.cartSignature !== signature) {
          box.dataset.cartSignature = signature;
          box.querySelector('[data-rdtn-related]').replaceChildren();
        }
        return;
      }
      let headline = rule.label;
      let progress = null;

      if (currency === 'USD' && rule.threshold !== null) {
        if (rule.threshold === 0) {
          headline = translate('cart_free_available');
          progress = 100;
        } else if (cart.total_price >= rule.threshold) {
          headline = translate('cart_unlocked', { amount: formatUsd(rule.threshold) });
          progress = 100;
        } else {
          const remaining = Math.max(0, rule.threshold - cart.total_price);
          headline = translate('cart_remaining', { amount: formatUsd(remaining) });
          progress = Math.max(0, Math.min(100, (cart.total_price / rule.threshold) * 100));
        }
      }

      let sub = `<a href="${shippingUrl}">${escapeHtml(translate('cart_details_link'))}</a>`;
      if (country === 'US') {
        sub = escapeHtml(translate('mainland_other')) + ' ' +
          `<a href="${shippingUrl}">${escapeHtml(translate('cart_details_link'))}</a>`;
      }

      const track = progress === null ? '' : `
        <div class="rdtn-shipping-cart-promo__track" aria-hidden="true">
          <span class="rdtn-shipping-cart-promo__fill" style="width:${progress}%"></span>
        </div>`;

      box.innerHTML = `
        <p class="rdtn-shipping-cart-promo__headline">${escapeHtml(headline)}</p>
        ${track}
        <p class="rdtn-shipping-cart-promo__sub">${sub}</p>
      `;
    });
  }

  async function syncCart() {
    if (syncInFlight) { syncPending = true; return; }
    syncInFlight = true;
    try {
      do {
        syncPending = false;
        const revision = syncRevision;
        try {
          const root = window.Shopify?.routes?.root || '/';
          const response = await fetch(root + 'cart.js', {
            credentials: 'same-origin', cache: 'no-store',
            headers: { Accept: 'application/json' }, signal: AbortSignal.timeout(10000)
          });
          if (!response.ok) throw new Error('Cart unavailable');
          const cart = await response.json();
          if (!Number.isFinite(cart.total_price) || cart.total_price < 0) throw new Error('Invalid cart total');
          if (revision !== syncRevision || syncPending) { syncPending = true; continue; }
          latestCart = cart;
          removeDuplicateAovNotes();
          renderCartMessage(cart);
          renderRelatedItems(cart, revision);
        } catch (_) {
          document.querySelectorAll('.rdtn-shipping-cart-promo').forEach((box) => box.remove());
        }
      } while (syncPending);
    } finally {
      syncInFlight = false;
    }
  }

  function scheduleSync(delay = 120) {
    syncRevision += 1;
    if (syncInFlight) syncPending = true;
    clearTimeout(syncTimer);
    syncTimer = setTimeout(() => {
      repairLegacyShippingCopy();
      renderProductMessage();
      removeDuplicateAovNotes();
      syncCart();
    }, delay);
  }

  function init() {
    repairLegacyShippingCopy();
    renderProductMessage();
    removeDuplicateAovNotes();
    syncCart();

    const observer = new MutationObserver((mutations) => {
      const relevant = mutations.some((mutation) => {
        const target = mutation.target instanceof Element ? mutation.target : mutation.target.parentElement;
        if (!target) return false;
        if (target.closest('.rdtn-shipping-cart-promo, .rdtn-pdp-shipping-note')) return false;
        return target.closest('#cart-drawer, main') !== null;
      });
      if (relevant) scheduleSync();
    });

    observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('pageshow', () => scheduleSync());
    document.addEventListener('cart-section:restored', () => scheduleSync());
    document.addEventListener('change', (event) => {
      if (event.target.closest?.('#cart-drawer, form[action*="/cart"]')) scheduleSync(250);
    });
    document.addEventListener('click', (event) => {
      if (event.target.closest?.('#cart-drawer, button[name="add"], [name="add"]')) scheduleSync(350);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
